/* ============================================================================
   OURO mega menu — desktop hover/click hardening.

   Fixes three issues with the stock <header-menu> behavior:

   1. Grace period on close: the stock component deactivates the submenu the
      instant the pointer leaves the menu item, so the panel vanishes before
      the user can reach it. We patch HeaderMenu.prototype.deactivate (the
      declarative on:pointerleave handler is resolved at event time, so a
      prototype patch always takes effect) to delay the close by 300ms, and
      cancel that close as soon as the pointer is back over the menu/panel.

   2. Self-healing open: section hydration can morph the menu DOM and reset
      aria-expanded while the component's internal `activeItem` state still
      points at the link — activate() then early-returns and hovering the item
      no longer opens the panel. If a mega link is hovered but not expanded a
      frame later, we open the panel manually (aria + the CSS height vars the
      clip-path animation needs).

   3. Click keeps the menu open: clicking a top-level link that owns a mega
      menu (PRODUCTS) must not navigate — it pins the panel open instead.
      Navigation lives inside the panel ("View All").
   ============================================================================ */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 1025px)';
  var CLOSE_DELAY = 300;

  function isDesktop() {
    return window.matchMedia(DESKTOP).matches;
  }

  function headerComponentOf(el) {
    return el.closest('header-component') || document.getElementById('header-component');
  }

  function submenuOf(link) {
    var li = link.closest('li');
    return li ? li.querySelector('[ref="submenu[]"]') : null;
  }

  function openPanel(link) {
    var headerComponent = headerComponentOf(link);
    var submenu = submenuOf(link);
    if (!headerComponent || !submenu) return;

    link.setAttribute('aria-expanded', 'true');
    var height = submenu.offsetHeight;
    headerComponent.style.setProperty('--submenu-height', height + 'px');
    headerComponent.style.setProperty('--full-open-header-height', height + headerComponent.offsetHeight + 'px');
  }

  /* Close any expanded mega link that is no longer hovered nor keyboard-
     focused. Backup for when the stock deactivate no-ops because its internal
     state desynced (it then leaves aria-expanded=true behind forever). */
  function sanityClose(menuEl) {
    var links = menuEl.querySelectorAll('.menu-list__link[aria-haspopup][aria-expanded="true"]');
    links.forEach(function (link) {
      var li = link.closest('li');
      var submenu = submenuOf(link);
      if (li && (li.matches(':hover') || li.contains(document.activeElement))) return;
      if (submenu && (submenu.matches(':hover') || submenu.contains(document.activeElement))) return;

      link.setAttribute('aria-expanded', 'false');
      var headerComponent = headerComponentOf(link);
      if (headerComponent) {
        headerComponent.style.setProperty('--submenu-height', '0px');
        headerComponent.style.setProperty('--full-open-header-height', '0px');
      }
    });
  }

  customElements.whenDefined('header-menu').then(function () {
    var HeaderMenu = customElements.get('header-menu');
    if (!HeaderMenu || HeaderMenu.prototype.__ouroPatched) return;
    HeaderMenu.prototype.__ouroPatched = true;

    var originalDeactivate = HeaderMenu.prototype.deactivate;

    HeaderMenu.prototype.deactivate = function (event) {
      if (!isDesktop()) return originalDeactivate.call(this, event);

      var self = this;
      clearTimeout(self.__ouroCloseTimer);
      self.__ouroCloseTimer = setTimeout(function () {
        self.__ouroCloseTimer = null;
        originalDeactivate.call(self, event);
        sanityClose(self);
      }, CLOSE_DELAY);
    };
  });

  /* Pointer back over the menu or its panel: cancel any pending close.
     (pointerover bubbles, pointerenter does not.) */
  document.addEventListener('pointerover', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var menuEl = target.closest('header-menu');
    if (menuEl && menuEl.__ouroCloseTimer) {
      clearTimeout(menuEl.__ouroCloseTimer);
      menuEl.__ouroCloseTimer = null;
    }

    if (!isDesktop()) return;

    /* Self-heal: hovered mega link that did not expand (stale component
       state). Check one frame later so the stock activate() runs first. */
    var link = target.closest('.menu-list__link[aria-haspopup]');
    if (!link) return;
    requestAnimationFrame(function () {
      if (link.getAttribute('aria-expanded') === 'true') return;
      if (!link.matches(':hover')) return;
      openPanel(link);
    });
  });

  /* Clicking a mega-menu parent link: with a mouse it navigates to the
     link of the menu item (e.g. PRODUCTS -> Chains); the panel already opens
     on hover. On touch screens (no hover) the first tap pins the panel open
     instead, so the submenu can still be reached. Capture phase so it wins
     over other handlers. */
  document.addEventListener(
    'click',
    function (event) {
      if (!isDesktop()) return;
      var target = event.target;
      if (!(target instanceof Element)) return;
      var link = target.closest('.menu-list__link[aria-haspopup]');
      if (!link) return;

      var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      var href = link.getAttribute('href');
      if (canHover && href && href !== '#') return;

      event.preventDefault();
      openPanel(link);
    },
    { capture: true }
  );
})();
